import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider/Divider'
import { SxProps, Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import { LightTooltip } from '@sales-center/components'

interface Props {
  disableCancel?: boolean
  disableSave?: boolean
  onSave?: () => void
  onCancel?: () => void
  primaryBtnLabel?: string
  secondaryBtnLabel?: string
  padding?: string
  footer?: React.ReactNode
  sx?: Record<string, SxProps<Theme> | string | number | undefined>
  children: React.ReactNode
  saveBtnDataTestId?: string
  cancelBtnDataTestId?: string
  containerTop?: string
  containerBottom?: string
  cancelBtnVariant?: string
  tidalThemeActive?: boolean
  noTextTransform?: boolean
  containerStyle?: Record<string, SxProps<Theme> | string | number | undefined>
  tidalMode?: boolean
  actionButtonHeight?: string
  saveBtnDisableInfo?: string
}

export function ClosingRequirementsForm(props: Props) {
  const {
    onSave,
    onCancel,
    disableCancel = false,
    disableSave = false,
    primaryBtnLabel = 'Save',
    secondaryBtnLabel = 'Cancel',
    padding,
    footer,
    sx,
    saveBtnDataTestId,
    cancelBtnDataTestId,
    cancelBtnVariant,
    tidalMode,
    actionButtonHeight,
    saveBtnDisableInfo,
    containerStyle,
    children,
  } = props

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        ...(containerStyle as object),
        ...(sx as object),
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          ...(padding ? { p: padding } : {}),
        }}
      >
        {children}
      </Box>

      {!!footer && (
        <>
          {!tidalMode && <Divider />}
          <Box sx={{ px: 2, py: 1.5 }}>{footer}</Box>
        </>
      )}

      {(onCancel || onSave) && (
        <>
          {!tidalMode && <Divider />}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              px: 2,
              py: 2,
              backgroundColor: 'background.paper',
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              paddingBottom: {
                xs: 'env(safe-area-inset-bottom)',
                sm: '20px',
              },
              ...(actionButtonHeight
                ? { '& .MuiButton-root': { height: actionButtonHeight } }
                : {}),
            }}
          >
            {onCancel && (
              <Button
                variant={cancelBtnVariant || tidalMode ? 'text' : 'outlined'}
                onClick={onCancel}
                data-test-id={
                  cancelBtnDataTestId
                    ? cancelBtnDataTestId
                    : 'sideDrawerCloseBtn'
                }
                disabled={disableCancel}
              >
                {secondaryBtnLabel}
              </Button>
            )}

            {onSave && (
              <LightTooltip title={saveBtnDisableInfo || ''}>
                <Typography component="span">
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={onSave}
                    disabled={disableSave}
                    data-test-id={saveBtnDataTestId ? saveBtnDataTestId : ''}
                  >
                    {primaryBtnLabel}
                  </Button>
                </Typography>
              </LightTooltip>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
